import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Table,
} from "reactstrap"
import { connect } from "react-redux"
import { useParams, Link, useNavigate } from "react-router-dom"
import { sendWhatsAppDocument } from "../../helpers/fakebackend_helper"
import toastr from "toastr"

import {
    setBreadcrumbItems,
    getQuoteDetail,
    convertQuoteToInvoice,
} from "../../store/actions"

const QuoteDetail = props => {
    const { id } = useParams()
    const navigate = useNavigate()
    document.title = "Détail Devis | CRM Entreprise"
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false)

    const breadcrumbItems = [
        { title: "Finances", link: "#" },
        { title: "Devis", link: "/finance/quotes" },
        { title: "Détail", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Détail Devis", breadcrumbItems)
        props.getQuoteDetail(id)
    }, [id])

    const quote = props.quote

    const statusColors = {
        'draft': 'secondary',
        'sent': 'info',
        'accepted': 'success',
        'rejected': 'danger',
        'expired': 'warning',
    }

    const statusLabels = {
        'draft': 'Brouillon',
        'sent': 'Envoyé',
        'accepted': 'Accepté',
        'rejected': 'Rejeté',
        'expired': 'Expiré',
    }

    const handleSendWhatsApp = async () => {
        if (!props.quote) return
        setSendingWhatsapp(true)
        try {
            await sendWhatsAppDocument({
                type: 'quote',
                id: props.quote.id
            })
            toastr.success("Devis envoyé par WhatsApp avec succès")
        } catch (error) {
            console.error("Erreur envoi WhatsApp:", error)
            toastr.error(error.response?.data?.message || "Erreur lors de l'envoi")
        } finally {
            setSendingWhatsapp(false)
        }
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD'
        }).format(amount)
    }

    if (!quote || quote.id?.toString() !== id) {
        return (
            <div className="container-fluid">
                <Card>
                    <CardBody>
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>
        )
    }

    return (
        <React.Fragment>
            <div className="container-fluid">
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <div>
                                        <h4 className="mb-2">Devis #{quote.quote_number}</h4>
                                        <Badge color={statusColors[quote.status]} className="font-size-12 p-2 px-3">
                                            {statusLabels[quote.status]}
                                        </Badge>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Link to="/finance/quotes">
                                            <Button color="light" size="sm">
                                                <i className="mdi mdi-arrow-left me-1"></i> Retour
                                            </Button>
                                        </Link>
                                        {quote.status !== 'accepted' && quote.status !== 'rejected' && (
                                            <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => {
                                                    if (window.confirm("Convertir ce devis en facture ?")) {
                                                        props.convertQuoteToInvoice(quote.id, navigate)
                                                    }
                                                }}
                                            >
                                                <i className="mdi mdi-swap-horizontal me-1"></i> Convertir en Facture
                                            </Button>
                                        )}
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={handleSendWhatsApp}
                                            disabled={sendingWhatsapp}
                                        >
                                            {sendingWhatsapp ? (
                                                <i className="bx bx-loader bx-spin font-size-16 align-middle"></i>
                                            ) : (
                                                <i className="mdi mdi-whatsapp me-1"></i>
                                            )}
                                        </Button>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            onClick={() => {
                                                const id = quote.id;
                                                const url = process.env.REACT_APP_API_URL + `/quotes/${id}/pdf`;
                                                // Pour le téléchargement avec auth, on peut utiliser fetch ou axios.get avec blob
                                                import("../../helpers/fakebackend_helper").then(helper => {
                                                    helper.getQuotePdf(id).then(response => {
                                                        const url = window.URL.createObjectURL(new Blob([response]));
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.setAttribute('download', `Devis-${quote.quote_number}.pdf`);
                                                        document.body.appendChild(link);
                                                        link.click();
                                                    });
                                                });
                                            }}
                                        >
                                            <i className="mdi mdi-printer me-1"></i> Télécharger PDF
                                        </Button>
                                    </div>
                                </div>

                                <hr />

                                <Row className="mt-4">
                                    <Col sm={6}>
                                        <h5 className="font-size-15 mb-3">Client</h5>
                                        <h6 className="font-size-14 mb-1">
                                            {quote.customer?.first_name} {quote.customer?.last_name}
                                        </h6>
                                        {quote.customer?.company_name && (
                                            <p className="mb-1 text-muted">{quote.customer?.company_name}</p>
                                        )}
                                        <p className="mb-1">{quote.customer?.email}</p>
                                        <p className="mb-0">{quote.customer?.phone}</p>
                                    </Col>
                                    <Col sm={6} className="text-sm-end">
                                        <div>
                                            <p className="mb-2">
                                                <strong>Date du devis:</strong> {new Date(quote.quote_date).toLocaleDateString('fr-FR')}
                                            </p>
                                            <p className="mb-2 text-danger">
                                                <strong>Valable jusqu'au:</strong> {new Date(quote.valid_until).toLocaleDateString('fr-FR')}
                                            </p>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="py-4">
                                    <h5 className="font-size-15 mb-3">Articles & Services</h5>
                                    <div className="table-responsive">
                                        <Table className="table-nowrap table-centered mb-0 border">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Description</th>
                                                    <th className="text-end" style={{ width: '100px' }}>Qté</th>
                                                    <th className="text-end" style={{ width: '150px' }}>Prix unit.</th>
                                                    <th className="text-end" style={{ width: '100px' }}>TVA</th>
                                                    <th className="text-end" style={{ width: '150px' }}>Total HT</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {quote.items && quote.items.map((item, idx) => (
                                                    <tr key={idx}>
                                                        <td>{item.description}</td>
                                                        <td className="text-end">{item.quantity}</td>
                                                        <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                        <td className="text-end">{item.tax_rate}%</td>
                                                        <td className="text-end fw-semibold">{formatCurrency(item.quantity * item.unit_price)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <Row className="mt-4 justify-content-end">
                                        <Col sm={6}>
                                            <div className="text-sm-end">
                                                <div className="mb-2">
                                                    <span className="me-3">Sous-total H.T:</span>
                                                    <span className="fw-semibold">{formatCurrency(quote.subtotal)}</span>
                                                </div>
                                                <div className="mb-2">
                                                    <span className="me-3">TVA Totale:</span>
                                                    <span className="fw-semibold">{formatCurrency(quote.tax_amount)}</span>
                                                </div>
                                                {quote.discount_amount > 0 && (
                                                    <div className="mb-2 text-danger">
                                                        <span className="me-3">
                                                            Remise {quote.discount_type === 'percentage' && `(${quote.discount_value}%)`}:
                                                        </span>
                                                        <span className="fw-semibold">
                                                            -{formatCurrency(quote.discount_amount)}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="border-top pt-2">
                                                    <h4 className="mb-0 text-primary">
                                                        <span className="me-3 fs-5 text-dark">Total TTC:</span>
                                                        <span>{formatCurrency(quote.total)}</span>
                                                    </h4>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {(quote.notes || quote.terms) && <hr />}

                                <Row>
                                    {quote.notes && (
                                        <Col sm={6}>
                                            <h6>Notes:</h6>
                                            <p className="text-muted small">{quote.notes}</p>
                                        </Col>
                                    )}
                                    {quote.terms && (
                                        <Col sm={6}>
                                            <h6>Conditions:</h6>
                                            <p className="text-muted small">{quote.terms}</p>
                                        </Col>
                                    )}
                                </Row>

                                {quote.status === 'accepted' && quote.invoice && (
                                    <div className="mt-4 p-3 bg-success-subtle border border-success rounded d-flex align-items-center justify-content-between">
                                        <div>
                                            <i className="mdi mdi-check-circle text-success font-size-24 me-2 align-middle"></i>
                                            <span className="fw-bold">Ce devis a été converti en facture.</span>
                                        </div>
                                        <Link to={`/finance/invoices/${quote.invoice.id}`}>
                                            <Button color="success" size="sm">
                                                Voir la Facture {quote.invoice.invoice_number}
                                            </Button>
                                        </Link>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    quote: state.Quotes.quoteDetail,
})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getQuoteDetail,
    convertQuoteToInvoice,
})(QuoteDetail)
